import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./HomePage.module.scss";

import Post from '../../components/Post/Post'
import CommentSection from '../../components/CommentSection/CommentSection'
import FeedComposer from '../../components/FeedComposer/FeedComposer';
import NewPostIndicator from '../../components/NewPostIndicator/NewPostIndicator';
import { useNavigate } from "react-router-dom";
import { useFeed } from "../../hooks/useFeed";
import { useNewPosts } from "../../hooks/useNewPosts";
import { useAlert } from "../../hooks/useAlert";
import { api } from "../../services/api";
import { SkeletonContainer } from '../../components/Skeleton';
import { HomeFeedSkeleton } from './components/HomeFeedSkeleton';
import type { FeedItem } from "../../types/Post/post";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { alert } = useAlert();
  const { items, loading, error, hasMore, loadMore, updateItem, removeItem, refresh, prependItems } = useFeed();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const feedContainerRef = useRef<HTMLDivElement | null>(null);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);

  // The newest displayed post's createdAt — used as the "since" reference for fetching new posts
  const newestTimestamp = items.length > 0 ? items[0].post.createdAt : null;

  const handleNewPostsLoaded = useCallback((posts: FeedItem[]) => {
    // Preserve scroll position when inserting at top
    const container = feedContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    prependItems(posts);

    // After DOM update, adjust scroll to maintain visual position
    requestAnimationFrame(() => {
      if (container) {
        const newScrollHeight = container.scrollHeight;
        const heightDiff = newScrollHeight - prevScrollHeight;
        if (heightDiff > 0) {
          window.scrollBy(0, heightDiff);
        }
      }
    });
  }, [prependItems]);

  const { newPostCount, loadNewPosts, loading: loadingNewPosts, reset: resetNewPosts } = useNewPosts(
    newestTimestamp,
    handleNewPostsLoaded
  );

  // Reset counter when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      resetNewPosts();
    };
  }, [resetNewPosts]);

  const handleOpenComments = (postId: string) => {
    setExpandedPostId((prev) => (prev === postId ? null : postId));
  };

  const handleDeletePost = async (postId: string) => {
    try {
      await api.social.deletePost(postId);
      removeItem(postId);
      if (expandedPostId === postId) {
        setExpandedPostId(null);
      }
    } catch (err) {
      console.error("Delete post error:", err);
      alert({ message: "Failed to delete post. Please try again.", type: 'error' });
    }
  };

  const handleEditPost = async (postId: string, newBody: string) => {
    try {
      const result = await api.social.editPost(postId, newBody);
      updateItem(postId, (item) => ({
        ...item,
        post: {
          ...item.post,
          body: result.body,
          updatedAt: result.updatedAt,
        },
      }));
    } catch (err) {
      console.error("Edit post error:", err);
      alert({ message: "Failed to edit post. Please try again.", type: 'error' });
    }
  };

  const handleLikeToggle = async (postId: string, nextLiked: boolean) => {
    // Optimistically update UI
    updateItem(postId, (item) => ({
      ...item,
      likedByMe: nextLiked,
      post: {
        ...item.post,
        likeCount: item.post.likeCount + (nextLiked ? 1 : -1),
      },
    }));

    try {
      if (nextLiked) {
        await api.social.likePost(postId);
      } else {
        await api.social.unlikePost(postId);
      }
    } catch (err) {
      // Revert on failure
      updateItem(postId, (item) => ({
        ...item,
        likedByMe: !nextLiked,
        post: {
          ...item.post,
          likeCount: item.post.likeCount + (nextLiked ? -1 : 1),
        },
      }));
      console.error("Like toggle error:", err);
    }
  };

  // Infinite scroll via IntersectionObserver
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      sentinelRef.current = node;
    },
    []
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  const handlePostCreated = useCallback(() => {
    refresh();
    resetNewPosts();
  }, [refresh, resetNewPosts]);

  return (
    <div className={styles.homePage} ref={feedContainerRef}>
      <FeedComposer onPostCreated={handlePostCreated} />
      <NewPostIndicator
        count={newPostCount}
        onClick={loadNewPosts}
        loading={loadingNewPosts}
      />
      <SkeletonContainer
        loading={loading && items.length === 0}
        skeleton={<HomeFeedSkeleton />}
        label="feed"
      >
        {/* Error message */}
        {error && (
          <div className={styles.errorState}>
            <p>Error loading feed: {error}</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && items.length === 0 && (
          <div className={styles.emptyState}>
            <p>No posts to show. Follow some users to see their posts here!</p>
          </div>
        )}

        {/* Feed items */}
        {items.map((item) => {
          const isExpanded = expandedPostId === item.post._id;
          return (
            <div
              className={`${styles.postContainer} ${isExpanded ? styles.postContainerExpanded : ""}`}
              key={item.post._id}
            >
              <Post
                item={item}
                onLikeToggle={handleLikeToggle}
                onDelete={handleDeletePost}
                onEdit={handleEditPost}
                onAuthorClick={(userId) => navigate(`/profile?user=${userId}`)}
                onOpenComments={handleOpenComments}
                commentsExpanded={isExpanded}
              />
              {isExpanded && (
                <CommentSection postId={item.post._id} />
              )}
            </div>
          );
        })}
      </SkeletonContainer>

      {/* Spinner while fetching next page */}
      {loading && items.length > 0 && (
        <div className={styles.spinnerContainer}>
          <div className={styles.spinner} />
        </div>
      )}

      {/* Invisible sentinel that triggers loading the next page */}
      {hasMore && <div ref={loadMoreRef} aria-hidden="true" />}
    </div>
  );
};

export default HomePage;
